import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './BannerSlidesForm.module.css';
import { ReactComponent as UploadIcon } from '../../images/Shared/Image.svg';
import { BannerService } from '../../services/BannerService';
import { showErrorToast, showSuccessToast } from '../../components/Shared/Toastify/toastHelper';
import { CircularProgress } from '@mui/material';
import ArchiveModal from '../../components/Shared/ArchiveModal/ArchiveModal';
import { UserContext } from '../../UserContext';

interface PublisherFormProps {
  mode: 'add' | 'edit' | 'view';
}

interface BannerData {
  title: string;
  imageUrl: File | null;
  existingImageUrl?: string;
  archived: boolean;
}

const BannerSlideForm: React.FC<PublisherFormProps> = ({ mode }) => {
  const { user } = useContext(UserContext);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const [bannerData, setBannerData] = useState<BannerData>({
    title: '',
    imageUrl: null,
    archived: false,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'archive' | 'delete'>('archive');
  const [isDragging, setIsDragging] = useState(false);

  const handleDelete = () => {
    setModalMode('delete');
    setModalOpen(true);
  };

  const handleArchive = () => {
    setModalMode('archive');
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!id) {
      showErrorToast("Id not found.");
      return;
    }

    try {
      if (modalMode === 'delete') {
        await BannerService.deleteBanner(id);
        showSuccessToast('Banner Slide deleted successfully');
        navigate('/banner-slides');
      } else if (modalMode === 'archive') {
        await BannerService.toggleArchive(id);
        showSuccessToast(bannerData.archived ? 'Banner Slide unarchived successfully' : 'Banner Slide archived successfully');
        navigate('/banner-slides');
      }
    } catch (error: any) {
      showErrorToast(`Error: ${modalMode === 'delete' ? 'deleting' : 'toggling archive'} banner: ` + error.message);
    }

    setModalOpen(false);
  };

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        if (!id) {
          throw new Error('error while fetching banner: no id');
        }
        const response = await BannerService.getBannerById(id);
        setBannerData({
          title: response.title,
          imageUrl: null,
          existingImageUrl: response.imageUrl,
          archived: response.archived
        });
        setImagePreview(response.imageUrl);
      } catch (error: any) {
        showErrorToast('Error fetching banner: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (mode !== 'add' && id) {
      fetchBanner();
    } else {
      setLoading(false);
    }
  }, [mode, id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBannerData((prevData) => ({
      ...prevData,
      title: e.target.value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setBannerData((prevData) => ({
        ...prevData,
        imageUrl: file,
      }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageButtonClick = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setBannerData((prevData) => ({
          ...prevData,
          imageUrl: file,
        }));
        setImagePreview(URL.createObjectURL(file));
      } else {
        showErrorToast('Please drop an image file');
      }
    }
  };

  const validateForm = (): boolean => {
    const missingFields: string[] = [];

    if (!bannerData.title.trim()) {
      missingFields.push('Title');
    }
    if (mode === 'add' && !bannerData.imageUrl) {
      missingFields.push('Image');
    }

    if (missingFields.length > 0) {
      showErrorToast(`The following fields are missing: ${missingFields.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setButtonLoading(true);
    const formData = new FormData();
    formData.append('title', bannerData.title);
    if (bannerData.imageUrl) {
      formData.append('image', bannerData.imageUrl);
    }

    try {
      if (mode === 'add') {
        await BannerService.createBanner(formData);
        showSuccessToast('Banner created successfully');
        navigate('/banner-slides');
      } else if (mode === 'edit' && id) {
        await BannerService.updateBanner(formData, id);
        showSuccessToast('Banner updated successfully');
        navigate(`/banner-slides/view/${id}`);
      }
    } catch (error: any) {
      if (mode === 'add') {
        showErrorToast('Error creating banner: ' + error.message);
      } else if (mode === 'edit' && id) {
        showErrorToast('Error updating banner: ' + error.message);
      }
    } finally {
      setButtonLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.background}>
        <div className={styles.Title}>Banner Information</div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <section className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Link</label>
              <textarea
                value={bannerData.title}
                onChange={handleInputChange}
                className={styles.input}
                disabled={mode === "view"}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>CoverImage</label>
              <div 
                className={`${styles.imageInputContainer} ${isDragging ? styles.dragging : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className={styles.previewImage} />
                )}

                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {!imagePreview && (
                  <>
                    <div>
                      <UploadIcon />
                    </div>
                    <p>upload or drop image</p>
                  </>
                )}
                {mode !== 'view' ? (
                  <button className={styles.addImageButton} onClick={handleImageButtonClick} type='button'>
                    Upload
                  </button>
                ) : null}
              </div>
            </div>
          </section>
          {mode !== 'view' && (
            <div className={styles.buttonContainer}>
              {mode === 'edit' && user?.role === 'admin' ? (
                <>
                  <button className={styles.DeleteButton} type="button" onClick={handleDelete}>
                    DELETE
                  </button>
                  <button className={styles.ArchiveButton} type="button" onClick={handleArchive}>
                    {bannerData.archived === false ? 'ARCHIVE' : "UNARCHIVE"}
                  </button>
                </>
              ) : null}
              <button className={styles.AddButton} type="submit">
                {buttonLoading ? <CircularProgress style={{ color: 'white' }} /> : mode === 'add' ? 'ADD' : 'UPDATE'}
              </button>
            </div>
          )}
        </form>
        <ArchiveModal
          setIsOpen={setModalOpen}
          isOpen={modalOpen}
          onConfirm={handleConfirm}
          onCancel={() => setModalOpen(false)}
          header={`Sure you want to ${modalMode === 'archive' ? bannerData.archived === false ? 'archive' : "unarchive" : 'delete'} this banner?`}
          parag={`Are you sure you want to ${modalMode === 'archive' ? bannerData.archived === false ? "archive" : 'unarchive' : 'delete'} this banner?`}
          buttonText={modalMode === 'archive' ? (bannerData.archived === false ? 'Archive' : 'Unarchive') : 'Delete'}
          mode={modalMode}
        />
      </div>
    </div>
  );
};

export default BannerSlideForm;
